import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import BasePopup from './reactjs-popup';
import { BaseProps, Mode, PopupIntentStyle } from '../common/props';

export declare type PopupEventType =
  | 'hover'
  | 'click'
  | 'focus'
  | 'right-click';

export declare type PopupPosition =
  | 'top left'
  | 'top center'
  | 'top right'
  | 'right top'
  | 'right center'
  | 'right bottom'
  | 'bottom left'
  | 'bottom center'
  | 'bottom right'
  | 'left top'
  | 'left center'
  | 'left bottom'
  | 'center center';

type Props = Omit<BaseProps, 'children' | 'style'> & {
  nested?: boolean;
  trigger?: JSX.Element | ((isOpen: boolean) => JSX.Element);
  arrow?: boolean;
  children: React.ReactNode | ((closePopup: () => void) => React.ReactNode);
  position?: PopupPosition | PopupPosition[];
  mode?: Mode;
  disabled?: boolean;
  on?: PopupEventType | PopupEventType[];
  intent?: PopupIntentStyle;
  mouseEnterDelay?: number;
  mouseLeaveDelay?: number;
  open?: boolean;
  onOpen?: (event?: React.SyntheticEvent) => void;
  // the popup can be closed depend on multiple factor: mouse click outside, keyboard esc, click a close button
  onClose?: (
    event?: React.SyntheticEvent | KeyboardEvent | TouchEvent | MouseEvent
  ) => void;
  closeOnDocumentClick?: boolean;
  closeOnEscape?: boolean;
  noStopPropagation?: boolean;
  noPreventDefault?: boolean;
  innerRef?: any;
};

/**
 * Popover - dropdown s nějakou nabídkou
 */
export const PopupIn: React.FC<Props> = ({
  className,
  children,
  on,
  disabled,
  intent = 'none',
  arrow,
  mode = 'default',
  trigger,
  mouseEnterDelay = 250,
  mouseLeaveDelay = 100,
  open,
  onOpen,
  onClose,
  closeOnDocumentClick,
  closeOnEscape,
  innerRef,
  position = ['bottom center', 'top center', 'right center', 'left center'],
  nested,
  ...other
}) => {
  return (
    <BasePopup
      {...other}
      ref={innerRef}
      nested={nested}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      closeOnDocumentClick={closeOnDocumentClick}
      closeOnEscape={closeOnEscape}
      on={on}
      mouseEnterDelay={mouseEnterDelay}
      mouseLeaveDelay={mouseLeaveDelay}
      className={classNames(className, Classes.POPUP, {
        [Classes.POPUP_EDIT]: mode === 'edit',
        [Classes.POPUP_HIDDEN]: !children,
        [Classes.POPUP_INTENT_DANGER]: intent === 'danger',
        [Classes.POPUP_INTENT_SUCCESS]: intent === 'success',
        [Classes.POPUP_INTENT_WARNING]: intent === 'warning',
        [Classes.POPUP_VIEW]: mode === 'view',
        [Classes.POPUP_DEFAULT]: mode === 'default',
      })}
      disabled={disabled}
      arrow={arrow !== undefined}
      trigger={trigger}
      position={position}
    >
      {children}
    </BasePopup>
  );
};

export const Popup = React.forwardRef<any, Props>((props, ref) => {
  return <PopupIn innerRef={ref} {...props} />;
});
